import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';

import {
    SCENARIO_TYPES,
    useApplyForRateCallback,
} from '@ratehub/mtg-common';
import {
    LegacyCityPropType,
    Form,
    getLanguageFromLocale,
    HiddenField,
    toDate,
    isWidget,
} from '@ratehub/base-ui';
import { INTENDED_USE, PAYMENT_FREQUENCIES } from '@ratehub/documents/mtg-journey-document/stable';

import PAGE_TEMPLATES from '../definitions/PageTemplates';
import { MORTGAGE_TYPES, MORTGAGE_TERMS, Messages as MORTGAGE_TYPE_MESSAGES } from '../definitions/MortgageTerms';
import fetchProductTableMortgagesRates from '../functions/fetchProductTableMortgagesRates';
import ProductTableHeader, { PRODUCT_TABLE_TITLE_ELEMENT_TYPES } from './ProductTableHeader';
import ProductTableMortgageFilteredAsyncContainer from './ProductTableMortgageFilteredAsyncContainer';
import ProductTableMortgagesTable from './ProductTableMortgagesTable';


function ProductTableMortgages({
    titleAs,
    hideV2Title,
    hideTimestamp = false,
    lastUpdatedDate,

    initialTerm = MORTGAGE_TERMS[4],
    initialType = MORTGAGE_TYPES.FIXED,
    initialScenario = SCENARIO_TYPES.PURCHASE,
    initialProvinceCode,
    initialCity,
    initialHomePrice = 400_000,
    initialDownPaymentPercent = 5,
    initialRefinanceMortgageBalance = 300_000,
    initialRenewMortgageBalance = 350_000,
    initialAdditionalFund = 0,
    initialPaymentFrequency = PAYMENT_FREQUENCIES.MONTHLY,
    initialOccupancy = INTENDED_USE.OWNER_OCCUPIED,
    initialCMHCInsurance = true,
    initialHELOC = 200_000,
    initialRenewAmortization = 20,
    initialPurchaseRefinanceAmortization = 25,
    initialRates,

    className,
    children,
}) {
    const intl = useIntl();
    const applyForRate = useApplyForRateCallback();
    const [ formState, setFormState ] = useState(null);

    const initialFilterValues = {
        term: initialTerm,
        type: initialType,
        scenario: initialScenario,
        province: initialProvinceCode,
        city: initialCity,
        rates: initialRates,
        homePrice: initialHomePrice,
        helocAmount: initialHELOC,
        renewMortgageBalance: initialRenewMortgageBalance,
        refinanceMortgageBalance: initialRefinanceMortgageBalance,
        downPayment: initialDownPaymentPercent ? { percent: initialDownPaymentPercent } : undefined,
        lastUpdatedDate: lastUpdatedDate,
        renewAmortization: initialRenewAmortization,
        purchaseRefinanceAmortization: initialPurchaseRefinanceAmortization,
        additionalFunds: initialAdditionalFund,
        paymentFrequency: initialPaymentFrequency,
        occupancy: initialOccupancy,
        isCMHC: initialCMHCInsurance,
    };

    const rateFetchingOptions = {
        city:  formState?.values?.scenario === SCENARIO_TYPES.HELOC
            ? formState?.values?.helocCity
            : formState?.values?.city,
        scenario: formState?.values?.scenario ?? SCENARIO_TYPES.PURCHASE,
        type: formState?.values?.type,
        term: formState?.values?.term,
        amortization: formState?.values?.scenario === SCENARIO_TYPES.RENEWAL
            ? formState?.values?.renewAmortization
            : formState?.values?.purchaseRefinanceAmortization,
        isRental: formState?.values?.scenario !== SCENARIO_TYPES.HELOC
            && (formState?.values?.occupancy === INTENDED_USE.RENTAL
                || formState?.values?.occupancy === INTENDED_USE.SECOND_HOME),
    };


    function handleRateClick(rate) {
        applyForRate({
            rate,
            rateFetchingOptions,
        });
    }

    return (
        //* The following div with class names is required for this component (when rendered as a widget) to register it's full height preventing margin collapsing between sibling elements
        <div className={classNames(
            {
                'rh-display-inline-block rh-width-100p': isWidget(),
            },
            className,
        )}
        >
            <ProductTableHeader
                title={(
                    <If condition={!hideV2Title}>
                        <Choose>
                            <When
                                condition={
                                    formState?.values?.scenario
                                        ? formState.values.scenario === SCENARIO_TYPES.HELOC
                                        : initialScenario === SCENARIO_TYPES.HELOC
                                }
                            >
                                <FormattedMessage
                                    id="web-component.ProductTableMortgagesV2.titleHELOC"
                                    defaultMessage="HELOC Mortgage Rates"
                                />
                            </When>
                            <Otherwise>
                                <FormattedMessage
                                    id="web-component.ProductTableMortgagesV2.title"
                                    defaultMessage="{term}-Year {type} Mortgage Rates"
                                    values={{
                                        type: intl.formatMessage(MORTGAGE_TYPE_MESSAGES[formState?.values?.type ?? initialType]),
                                        term: (formState?.values?.term ?? initialTerm) / 12,
                                    }}
                                />
                            </Otherwise>
                        </Choose>
                    </If>
                )}
                titleAs={titleAs}
                lastUpdatedDate={hideTimestamp ? undefined : toDate(lastUpdatedDate)}
            />

            <>
                {children}
            </>

            <Form onCreateFormState={setFormState}>
                <HiddenField
                    name="language"
                    value={getLanguageFromLocale(intl.locale)}
                />

                <ProductTableMortgageFilteredAsyncContainer
                    values={initialFilterValues}

                    fetchedValueName="rates"
                    initialFetchedValue={initialFilterValues.rates}

                    getFormValues={getFormValuesForFetch}
                    onFetchValue={fetchProductTableMortgagesRates}
                    columnCount={formState?.values?.scenario !== SCENARIO_TYPES.HELOC ? 4 : 3}
                    debounce={400}
                >
                    {(values) => (
                        <ProductTableMortgagesTable
                            scenario={values?.scenario ?? initialScenario ?? SCENARIO_TYPES.PURCHASE}
                            rates={values?.rates ?? initialRates ?? []}
                            handleRateClick={handleRateClick}
                        />
                    )}
                </ProductTableMortgageFilteredAsyncContainer>
            </Form>
        </div>
    );
}

ProductTableMortgages.propTypes = {
    titleAs: PropTypes.oneOf(PRODUCT_TABLE_TITLE_ELEMENT_TYPES),
    hideV2Title: PropTypes.bool,
    hideTimestamp: PropTypes.bool,
    lastUpdatedDate: PropTypes.string,

    initialTerm: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
    initialType: PropTypes.string,
    initialScenario: PropTypes.string,
    initialProvinceCode: PropTypes.string,
    initialHomePrice: PropTypes.number,
    initialDownPaymentPercent: PropTypes.number,
    initialRenewMortgageBalance: PropTypes.number,
    initialRefinanceMortgageBalance: PropTypes.number,
    initialHELOC: PropTypes.number,
    initialCity: LegacyCityPropType,
    initialAdditionalFund: PropTypes.number,
    initialPaymentFrequency: PropTypes.oneOf(Object.values(PAYMENT_FREQUENCIES)),
    initialOccupancy: PropTypes.oneOf(Object.values(INTENDED_USE)),
    initialCMHCInsurance: PropTypes.bool,
    initialRenewAmortization: PropTypes.number,
    initialPurchaseRefinanceAmortization: PropTypes.number,
    initialRates: PropTypes.arrayOf(PropTypes.object),

    className: PropTypes.string,
    children: PropTypes.node,
};

function getFormValuesForFetch(values) {
    return {
        scenario: values?.scenario,
        term: values?.term,
        type: values?.type,
        city: values?.city,
        helocCity: values?.helocCity,
        homePrice: values?.homePrice,
        downPayment: values?.downPayment?.dollars,
        downPaymentPercent: values?.downPayment?.percent,
        renewMortgageBalance: values?.renewMortgageBalance,
        refinanceMortgageBalance: values?.refinanceMortgageBalance,
        helocAmount: values?.helocAmount,
        additionalFunds: values?.additionalFunds,
        paymentFrequency: values?.paymentFrequency,
        occupancy: values?.occupancy,
        isCMHC: values?.isCMHC,
        renewAmortization: values?.renewAmortization,
        purchaseRefinanceAmortization: values?.purchaseRefinanceAmortization,
        language: values?.language,
    };
}


ProductTableMortgages.blockKey = 'rh/mortgage-rate-table';
ProductTableMortgages.requiresLayoutRow = true;
ProductTableMortgages.supportedPageTypes = [ PAGE_TEMPLATES.mortgages ];

export default observer(ProductTableMortgages);
